import * as authActions from '~/actions/auth.actions';
import { ActionKeys } from '~/actions/constants';
import { SHIPPO_LASTSEEN_COOKIE_NAME } from '~/constants/cookies';
import { ADDITIONAL_INFORMATION_GTM_RESULTS } from '~/constants/local-storage-keys';
import * as Cookies from 'js-cookie';
import * as TagManager from 'react-gtm-module';
import { call, takeLatest } from 'redux-saga/effects';
import { GTMAdditionalInformation, GTMDataLayer } from '~/types/GTM';
import User, { getFullName } from '~/types/User';

function getIsImpersonateCookie() {
  return Cookies.get('is_impersonate') || 'false';
}

export function* isImpersonating() {
  const impersonatingCookieVal: string = yield call(getIsImpersonateCookie);
  return impersonatingCookieVal === 'true';
}

export function* createDataLayer(currentUser: User) {
  const storedAdditionalInformation: GTMAdditionalInformation =
    Cookies.getJSON(ADDITIONAL_INFORMATION_GTM_RESULTS) || {};
  const gtmDataLayer: GTMDataLayer & GTMAdditionalInformation = {
    userId: currentUser.id,
    userEmail: currentUser.email,
    userFullName: getFullName(currentUser),
    isImpersonating: yield call(isImpersonating),
    revenueTierName: currentUser.revenueTierName || '5',
    storesEnabled: [],
    availableCarriers: [],
    dateJoined: currentUser.dateJoined.toISOString(),
    lifetimeVolSegmentMin: currentUser.lifetimeVolSegmentMin || 0,
    lifetimeVolSegmentMax: currentUser.lifetimeVolSegmentMax || 0,
    groupName: currentUser.groupName || '0',
    loginUserId: currentUser.loginUserId,
    ...storedAdditionalInformation,
    event: 'USER_DATA_POPULATED',
  };

  return gtmDataLayer;
}

export function getGTMConfig() {
  const { id: gtmId, dataLayer: dataLayerName, auth, preview, enabled } = CONFIG.gtm;
  return { gtmId, dataLayerName, auth, preview, enabled };
}

export function* initializeAnonymousGTM() {
  let anonymousGTMTriggered = true;
  const gtmConfig: ReturnType<typeof getGTMConfig> = yield call(getGTMConfig);
  if (!gtmConfig.enabled) {
    return;
  }

  while (anonymousGTMTriggered) {
    const tagManagerArgs = { ...gtmConfig };
    yield call([TagManager, TagManager.initialize], tagManagerArgs);
    anonymousGTMTriggered = false;
  }
}

export function* initializeGTM(action: ReturnType<typeof authActions.userDidAuthenticate>) {
  const { currentUser } = action.payload;

  // Set a last seen cookie for user identification on marketing site
  const isUserImpersonating: boolean = yield call(isImpersonating);
  if (!isUserImpersonating) {
    const today = new Date();
    yield call(
      Cookies.set,
      SHIPPO_LASTSEEN_COOKIE_NAME,
      JSON.stringify({
        id: currentUser.id,
        lastSeen: today.toISOString().substring(0, 10),
        revenueTierName: currentUser.revenueTierName || '5',
        storesEnabled: [],
        availableCarriers: [],
        dateJoined: currentUser.dateJoined.toISOString().substring(0, 10),
        lifetimeVolSegmentMin: currentUser.lifetimeVolSegmentMin || 0,
        lifetimeVolSegmentMax: currentUser.lifetimeVolSegmentMax || 0,
      }),
      {
        domain: CONFIG.shippoRootUrl,
        expires: 365,
      },
    );
  }

  const gtmConfig: ReturnType<typeof getGTMConfig> = yield call(getGTMConfig);

  if (!gtmConfig.enabled) {
    return;
  }

  const dataLayer: GTMDataLayer = yield call(createDataLayer, currentUser);
  const tagManagerArgs = { ...gtmConfig, dataLayer };

  yield call([TagManager, TagManager.initialize], tagManagerArgs);
}

export default [
  takeLatest(ActionKeys.INITIALIZE_ANONYMOUS_TAG_MANAGER, initializeAnonymousGTM),
  takeLatest(ActionKeys.USER_DID_AUTHENTICATE, initializeGTM),
];
